// BranchSelect — the branch/tag/idea-lane picker modal.
//
// A near-clone of the omnisearch modal (`./search`): same WASM
// distance search engine (`../omnisearch`) for fuzzy type-ahead, same
// modal a11y/animation helpers, and the same debounced input and row
// highlight (`./search`). Differences: items carry a `mode`
// (patch vs navigate) and a `badge` (lane/idea), the results are
// rendered here (omnisearch's renderer is keyed to its own categories),
// and a chosen item routes via `branchSelectChoose` so the server can
// push_patch or push_navigate as the page's item builder decided.
//
// The trigger is a plain `phx-click={Events.branch_select_toggle()}`
// button in the layout — no trigger hook. Closing without choosing
// re-sends the toggle (flipping the server's `branch_select_open` off).

import escapeHtml from "escape-html";
import * as Engine from "../omnisearch";
import type { LiveViewHook } from "./types";
import type { BranchSelectPushEvent } from "../../../.dox/ui";
import { illuminateCssClassModal } from "../../../.dox/ui";
import {
  activateModalA11y,
  keepModalFocus,
  modalOpen,
  modalClose,
  modalCleanup,
  restoreModalFocus,
  syncModalShellState,
  trapModalFocus,
} from "./modal";
import { bindDebouncedInput, highlightItem } from "./search";

const APP_SHELL = illuminateCssClassModal("appShell");

interface BranchItem {
  readonly category: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly url: string;
  readonly mode: string;
  readonly badge: string | null;
}

type Choice = { readonly url: string; readonly mode: string };

// Goto lets you type an arbitrary branch/tag/SHA that isn't in the list
// and navigate straight to it (Enter with no matching item). The page
// supplies a URL template with a `__REF__` placeholder + the transition
// mode; the hook substitutes the typed ref. Snake-case keys because the
// payload arrives JSON-encoded from the server's atom-keyed map.
type Goto = { readonly url_template: string; readonly mode: string };

interface BranchSelectHook extends LiveViewHook<BranchSelectPushEvent> {
  _items: readonly BranchItem[];
  _goto: Goto | null;
  _selected: number;
  _closing: boolean;
  _debounceTimer: ReturnType<typeof setTimeout> | null;
  _restoreFocusEl: HTMLElement | null;
  _docEsc: (e: KeyboardEvent) => void;
  _focusTrap: (e: KeyboardEvent) => void;
  _focusGuard: (e: FocusEvent) => void;
  _close: (choice?: Choice) => void;
  _render: (query: string) => void;
}

const badgeHtml = (badge: string | null): string => {
  if (badge === "lane")
    return `<span class="ml-2 shrink-0 rounded-full bg-teal/20 text-teal text-[10px] px-1.5 py-0.5">lane</span>`;
  if (badge === "idea")
    return `<span class="ml-2 shrink-0 rounded-full bg-yellow/20 text-yellow text-[10px] px-1.5 py-0.5">idea</span>`;
  return "";
};

const rowHtml = (it: BranchItem, titleHtml?: string): string =>
  `<a data-branch-item data-url="${escapeHtml(it.url)}" data-mode="${escapeHtml(it.mode)}" ` +
  `class="flex items-center px-4 py-2 cursor-pointer">` +
  `<span class="min-w-0 truncate text-xs text-text">${titleHtml ?? escapeHtml(it.title)}</span>` +
  badgeHtml(it.badge) +
  (it.subtitle
    ? `<span class="ml-2 min-w-0 truncate text-xs text-text-muted">${escapeHtml(it.subtitle)}</span>`
    : "") +
  `</a>`;

const choiceOf = (el: HTMLElement): Choice => ({
  url: el.dataset["url"] ?? "",
  mode: el.dataset["mode"] ?? "navigate",
});

// Substitute the typed ref into a goto template's `__REF__` placeholder,
// URL-encoding it (a SHA is hex-safe; a branch name may contain `/`).
// Exported for unit testing the goto path without a DOM.
export const buildGotoUrl = (template: string, ref: string): string =>
  template.replace("__REF__", encodeURIComponent(ref.trim()));

export const BranchSelect = {
  mounted(this: BranchSelectHook) {
    this._items = [];
    this._goto = null;
    this._selected = 0;
    this._closing = false;

    const input = this.el.querySelector<HTMLInputElement>("input");
    const panel = this.el.querySelector<HTMLElement>(".modal-panel");
    const shell = document.querySelector<HTMLElement>(`.${APP_SHELL}`);

    this._restoreFocusEl = activateModalA11y(this.el, panel);
    modalOpen(shell, panel);

    this._close = (choice?: Choice): void => {
      if (this._closing) return;
      this._closing = true;
      modalClose(this.el, () => {
        if (choice)
          this.pushEvent("branchSelectChoose", {
            url: choice.url,
            mode: choice.mode,
          });
        // Closing without a choice: flip the server's open flag back off
        // by re-sending the toggle.
        else this.pushEvent("branchSelectToggle", {});
      });
    };

    this._render = (query: string): void => {
      const results = this.el.querySelector<HTMLElement>(
        "#branch-select-results",
      );
      if (!results) return;

      const q = query.trim();
      const html =
        q === ""
          ? this._items.map((it) => rowHtml(it)).join("")
          : // No cap: the empty-query path renders every item, so a
            // filtered search must too — the engine's default cap of 50
            // silently truncated the match list on large repos (C14b).
            Engine.scoreItems(this._items, q, this._items.length)
              .map((r) => rowHtml(r.item, r.titleHtml))
              .join("");

      results.innerHTML =
        html ||
        `<div class="px-4 py-4 text-xs text-text-muted text-center">No matches.</div>`;
      this._selected = 0;
      highlightItem(
        results.querySelectorAll<HTMLElement>("[data-branch-item]"),
        0,
      );
    };

    // One listener for every row, present and future. A listener per row,
    // re-added on every render, was a few thousand closures per keystroke
    // on a repo with that many refs.
    this.el.addEventListener("click", (e: Event) => {
      const row =
        e.target instanceof Element
          ? e.target.closest<HTMLElement>("[data-branch-item]")
          : null;
      if (!row) return;
      e.preventDefault();
      this._close(choiceOf(row));
    });

    this.handleEvent("branchSelectData", (payload: unknown) => {
      const { items, goto } = payload as { items?: BranchItem[]; goto?: Goto };
      this._items = items ?? [];
      this._goto = goto ?? null;
      this._render(input?.value ?? "");
    });

    // Scoring and rendering every ref is the expensive part, so it waits
    // for the typing to pause, as the omnisearch does.
    this._debounceTimer = null;
    if (input)
      bindDebouncedInput(input, this, (query) => {
        this._render(query);
      });

    this.el.addEventListener("keydown", (e: KeyboardEvent) => {
      // Enter must act on what is TYPED, not on the list from before the
      // last pause: render now if a render is still pending.
      if (e.key === "Enter" && this._debounceTimer !== null && input) {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = null;
        this._render(input.value);
      }
      const rows = this.el.querySelectorAll<HTMLElement>("[data-branch-item]");
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this._selected = Math.min(this._selected + 1, rows.length - 1);
        highlightItem(rows, this._selected);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this._selected = Math.max(this._selected - 1, 0);
        highlightItem(rows, this._selected);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const row = rows[this._selected];
        if (row) {
          this._close(choiceOf(row));
          return;
        }
        // No matching item — treat the typed text as an arbitrary
        // branch/tag/SHA and go straight to it via the page's goto
        // template. handle_params validates the ref server-side and
        // falls back to the default branch if it's garbage.
        const typed = (input?.value ?? "").trim();
        if (typed !== "" && this._goto) {
          this._close({
            url: buildGotoUrl(this._goto.url_template, typed),
            mode: this._goto.mode,
          });
        }
      }
    });

    const backdrop = this.el.querySelector(".modal-backdrop");
    if (backdrop)
      backdrop.addEventListener("click", (e: Event) => {
        e.stopPropagation();
        this._close();
      });

    this._docEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        this._close();
      }
    };
    document.addEventListener("keydown", this._docEsc);

    this._focusTrap = (e: KeyboardEvent): void => {
      trapModalFocus(e, this.el, panel);
    };
    this.el.addEventListener("keydown", this._focusTrap);

    this._focusGuard = (e: FocusEvent): void => {
      keepModalFocus(this.el, panel, e.target);
    };
    document.addEventListener("focusin", this._focusGuard);
  },

  updated(this: BranchSelectHook) {
    const input = this.el.querySelector<HTMLInputElement>("input");
    if (input && document.activeElement !== input) input.focus();
    syncModalShellState();
  },

  destroyed(this: BranchSelectHook) {
    if (this._debounceTimer !== null) clearTimeout(this._debounceTimer);
    this._debounceTimer = null;
    this._closing = false;
    document.removeEventListener("keydown", this._docEsc);
    this.el.removeEventListener("keydown", this._focusTrap);
    document.removeEventListener("focusin", this._focusGuard);
    modalCleanup();
    restoreModalFocus(this._restoreFocusEl);
  },
};
